import { Accordion, Button, Chip, Divider, IconNode, Typography } from '@components/base';
import { Avatar } from '@components/common';
import Images from '@assets/images';
import css from './index.module.scss';
import Footer from '../components/footer';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import BLDetailsModal from './BLDetailsModal';
import { getFormattedDate } from '@helpers/utils';
import { ITaskTabProps } from '@helpers/types/task-tabs';
import { Link } from 'react-router-dom';
import { TRANSPORT_CONFIG } from './transportConfig';
import AWBDetailsModal from './AWBDetailsModal';
import { TRANSPORTATION_MODE } from '@helpers/types/order';
import { TRANSPORTATION_MODE_ENUM } from '@helpers/constants';

interface Props {
  taskData: Partial<ITaskTabProps>;
  onComplete?: () => void;
}

const AddDocumentDetails = ({ taskData, onComplete }: Props) => {
  const [open, setOpen] = useState(false);
  const { illustrationEdit, alertSuccessFaint } = Images;

  const mode: TRANSPORTATION_MODE = (taskData.transportation_mode ??
    TRANSPORTATION_MODE_ENUM.SEA) as TRANSPORTATION_MODE;
  const getCompleteStatus = taskData.is_completed;
  const [taskIsCompleted, setTaskIsCompleted] = useState<boolean | undefined>(getCompleteStatus);

  const { taskName, schema, defaultValues } = TRANSPORT_CONFIG[mode];

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues as any,
    shouldUnregister: true
  });

  const handleStatusFromModal = (data: boolean) => {
    setTaskIsCompleted(data);
  };

  const ModalComponent = mode === TRANSPORTATION_MODE_ENUM.AIR ? AWBDetailsModal : BLDetailsModal;

  return (
    <>
      <div className={css.mainWrapper}>
        <Accordion defaultExpanded>
          <Accordion.Header className={css.accordionHeader}>
            <div className={css.header}>
              <div className={css.wrapper}>
                {taskData.is_completed ? (
                  <IconNode src={alertSuccessFaint} className={css.successIcon} />
                ) : (
                  <Avatar src={illustrationEdit} alt="edit-icon" />
                )}

                <Link to={`/order/${taskData.order}`} style={{ textDecoration: 'none' }}>
                  <Typography variant="h5" className={css.underlineOnHover}>
                    #{taskData.readable_task_id} – Add {taskName} Details
                  </Typography>
                </Link>
              </div>
              <div className={css.pocWrapper}>
                {!getCompleteStatus && taskIsCompleted === false && (
                  <Chip
                    label="Add"
                    variant="outlined"
                    clickable={true}
                    className={css.uploadButton}
                    onClick={() => setOpen(true)}
                  />
                )}
                {(!getCompleteStatus || !taskIsCompleted) && <Divider className={css.divider} />}
              </div>
            </div>
          </Accordion.Header>

          {taskData.is_completed && (
            <Accordion.Content>
              {mode === TRANSPORTATION_MODE_ENUM.AIR ? (
                <div className={css.bodyWrapper}>
                  <Typography variant="label">AWB Service Name:</Typography>
                  <Typography variant="p">{taskData.awb_service_name}</Typography>
                  <br />
                  <Typography variant="label">AWB Number:</Typography>
                  <Typography variant="p">{taskData.awb_number}</Typography>
                  <br />
                  <Typography variant="label">AWB Date:</Typography>
                  <Typography variant="p">{getFormattedDate(taskData.awb_date)}</Typography>
                </div>
              ) : (
                <div className={css.bodyWrapper}>
                  <Typography variant="label">BL Number:</Typography>
                  <Typography variant="p">{taskData.bl_number}</Typography>
                  <br />
                  <Typography variant="label">BL Date:</Typography>
                  <Typography variant="p">{getFormattedDate(taskData.bl_date)}</Typography>
                  {taskData.bl_type && (
                    <>
                      <br />
                      <Typography variant="label">BL Type:</Typography>
                      <Typography variant="p">{taskData.bl_type}</Typography>
                    </>
                  )}
                </div>
              )}
            </Accordion.Content>
          )}
        </Accordion>

        <Footer
          createdDate={taskData.created_at as Date}
          updatedDate={taskData.updated_at as Date}
        />
      </div>

      <FormProvider {...methods}>
        <ModalComponent
          open={open}
          onClose={() => setOpen(false)}
          taskData={taskData}
          onComplete={onComplete}
          sendStatusToCard={handleStatusFromModal}
          taskName={taskName}
          transportation_mode={mode}
        />
      </FormProvider>
    </>
  );
};

export default AddDocumentDetails;
